import ReactQuill from 'react-quill'
import { Storage } from 'aws-amplify';
import uuid from 'react-uuid';
import config from './index';
import { convertToWebP } from '../CommonComponent/convertToWebp';

const Delta = ReactQuill.Quill.import('delta');

export const getDescriptionImageUrls = (html) => {
    if (!html) return [];
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    return Array.from(doc.querySelectorAll('img'))
        .map(img => img.getAttribute('src'))
        .filter(src => src && src.includes('DomainName/Auctions/descriptions/'));
};

export const getOrphanedDescriptionImageKeys = (allUploadedUrls, currentDescription) => {
    const currentUrls = new Set(getDescriptionImageUrls(currentDescription));
    return allUploadedUrls
        .filter(url => !currentUrls.has(url))
        .map(url => {
            const index = url.indexOf('DomainName/');
            return index !== -1 ? url.substring(index) : url;
        });
};

export const RichEditorFormats = [
    'header', 'font', 'size',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet', 'indent',
    'align',
    'link', 'image', 'video'
];

export const fonts = [
    'Arimo',
    'Comic-Neue',
    'Courier-Prime',
    'Gothic-A1',
    'Lato',
    'Montserrat',
    'Open-Sans',
    'Roboto',
    'Tinos',
    'Inter',
];

// Inline SVG: grey rounded rect with "Uploading..." text
const LOADING_PLACEHOLDER = [
    'data:image/svg+xml;utf8,',
    '<svg width="120" height="80" viewBox="0 0 120 80" xmlns="http://www.w3.org/2000/svg">',
    '<rect width="120" height="80" rx="6" fill="%23f0f0f0"/>',
    '<text x="50%" y="50%" font-size="12" font-family="sans-serif"',
    ' fill="%23999" text-anchor="middle" dominant-baseline="middle">',
    'Uploading...</text></svg>',
].join('');

export const getQuillModules = (imageTracker, onUploadStateChange, onImageError) => ({
    toolbar: {
        container: [
            [{ font: fonts }],
            [{size: []}],
            ['bold', 'italic', 'underline', 'strike'],
            [{'list': 'ordered'}, {'list': 'bullet'}, {'indent': '-1'}, {'indent': '+1'}],
            [{ 'align': [] }],
            ['link', 'image', 'video'],
            ['clean']
        ],
        handlers: {
            image: function () {
                const quill = this.quill;
                const input = document.createElement('input');
                input.setAttribute('type', 'file');
                input.setAttribute('accept', 'image/jpeg,image/png,image/jpg');
                input.click();
                input.onchange = async () => {
                    const file = input.files[0];
                    if (!file) return;
                    if (file.size > 5 * 1024 * 1024) {
                        if (onImageError) onImageError('Image must be smaller than 5MB');
                        return;
                    }
                    // Insert placeholder directly into the DOM, bypassing Quill's embed path
                    // so the clipboard data: matcher doesn't strip it
                    const range = quill.getSelection(true);
                    quill.insertEmbed(range.index, 'image', LOADING_PLACEHOLDER, 'user');
                    quill.setSelection(range.index + 1);
                    // Tag with a data attribute so we can find it reliably
                    const insertedImgs = quill.root.querySelectorAll('img[src]');
                    let placeholderImg = null;
                    insertedImgs.forEach(img => {
                        if (img.getAttribute('src') === LOADING_PLACEHOLDER) {
                            img.setAttribute('data-uploading', 'true');
                            placeholderImg = img;
                        }
                    });
                    quill.disable();
                    if (onUploadStateChange) onUploadStateChange(true);
                    try {
                        const compressed = await convertToWebP(file);
                        const filePath = `DomainName/Auctions/descriptions/images/${uuid()}/${compressed.name}`;
                        const result = await Storage.put(filePath, compressed, {
                            contentType: compressed.type,
                        });
                        const url = `${config.Storage.CDN}${result.key}`;
                        if (imageTracker?.current) {
                            imageTracker.current.push(url);
                        }
                        const img = quill.root.querySelector('img[data-uploading="true"]');
                        if (img) {
                            img.setAttribute('src', url);
                            img.removeAttribute('data-uploading');
                        }
                    } catch (err) {
                        console.error('Image upload failed:', err);
                        const img = quill.root.querySelector('img[data-uploading="true"]');
                        if (img) img.remove();
                    } finally {
                        quill.enable();
                        if (onUploadStateChange) onUploadStateChange(false);
                    }
                };
            }
        }
    },
    clipboard: {
        matchers: [
            ['IMG', function (node, delta) {
                const src = node.getAttribute('src');
                if (src && src.startsWith('data:') && !node.getAttribute('data-uploading')) {
                    return new Delta();
                }
                return delta;
            }]
        ]
    }
});

const Font = ReactQuill.Quill.import('formats/font');
Font.whitelist = fonts;
ReactQuill.Quill.register(Font, true);